import { useEffect, useState } from 'react';
import { usePathname } from 'core/i18n/navigation';
import { getStore, storeKeys } from 'core/storage/store';
import { BannerVariant } from '../components/Banner';

// The promo currently shown in the homepage banner.
const BANNER_VARIANT: BannerVariant = 'pro';

// Once dismissed, the banner stays hidden for this long, then re-shows.
const BANNER_DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface UseBannerResult {
  /** The promo variant to render. */
  variant: BannerVariant;
  /** Whether the banner should currently be shown. */
  showBanner: boolean;
  /** Dismiss the banner (persisted for the TTL). */
  dismissBanner: () => void;
}

/**
 * Owns the homepage announcement banner's visibility: it only runs on the
 * homepage and can be dismissed, with the dismissal persisted as a timestamp in
 * localStorage that expires after a TTL so the banner re-shows. Reading in the
 * initializer is safe because the header renders client-side only (NoSSRWrapper).
 */
export const useBanner = (): UseBannerResult => {
  const pathname = usePathname();

  // The header is now server-rendered (App Router SiteChrome), so the banner's
  // visibility must NOT depend on client-only state during render. We start
  // hidden (matching the server), then read the dismissal from storage after
  // mount — so server and first client render agree (no hydration mismatch).
  const [mounted, setMounted] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setMounted(true);
    const dismissedAt = getStore().getItem(storeKeys.hasDismissedBanner);
    if (
      typeof dismissedAt === 'number' &&
      Date.now() - dismissedAt < BANNER_DISMISS_TTL_MS
    ) {
      setDismissed(true);
    }
  }, []);

  const dismissBanner = () => {
    getStore().setItem(storeKeys.hasDismissedBanner, Date.now());
    setDismissed(true);
  };

  return {
    variant: BANNER_VARIANT,
    showBanner: mounted && pathname === '/' && !dismissed,
    dismissBanner,
  };
};
